/* eslint-disable @typescript-eslint/no-explicit-any */
import auth from 'src/auth';
import { CANCEL } from 'redux-saga';
import type { RequestConfig, FetchResponse, FetchError, CancelablePromise } from 'src/types/fetch';

// Re-export types for convenience
export type { RequestConfig, FetchResponse, FetchError, CancelablePromise };

/**
 * Creates a structured fetch error matching axios error format
 * @param message - Error message
 * @param response - Optional Response object
 * @param config - Optional request configuration
 * @returns Structured FetchError
 */
function createFetchError(message: string, response?: Response, config?: RequestConfig): FetchError {
    const error = new Error(message) as FetchError;
    error.name = 'FetchError';
    if (response) {
        error.response = {
            data: null, // Will be populated by caller
            status: response.status,
            statusText: response.statusText,
        };
    }
    error.config = config;
    return error;
}

/**
 * Makes an HTTP request using native fetch API with axios-like interface
 * Supports authentication, request cancellation, and automatic JSON parsing
 *
 * @param config - Request configuration
 * @param config.url - Target URL
 * @param config.method - HTTP method (default: GET)
 * @param config.data - Request body data
 * @param config.headers - Additional headers
 * @param config.responseType - Expected response type (default: json)
 * @param config.includeToken - Whether to include auth token (default: false)
 * @returns Cancelable promise with fetch response
 */
export function makeFetchRequest<T = any>({
    url,
    method = 'GET',
    data,
    headers = {},
    responseType = 'json',
    includeToken = false,
}: RequestConfig): CancelablePromise<FetchResponse<T>> {
    const abortController = new AbortController();

    // Setup headers
    const fetchHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        ...headers,
    };

    // Add auth token if needed
    if (includeToken && auth.isLoggedIn()) {
        fetchHeaders.Authorization = `Bearer ${auth.getAccessToken()}`;
    }

    // Handle FormData (remove Content-Type to let browser set it with boundary)
    if (data instanceof FormData) {
        delete fetchHeaders['Content-Type'];
    }

    // Setup fetch options
    const fetchOptions: RequestInit = {
        method,
        headers: fetchHeaders,
        signal: abortController.signal,
    };

    // Add body for non-GET requests
    if (data && method !== 'GET') {
        fetchOptions.body = data instanceof FormData ? data : JSON.stringify(data);
    }

    // Make the request
    const fetchPromise = fetch(url, fetchOptions)
        .then(async (response) => {
            // Parse response based on type
            let responseData: any;
            if (responseType === 'blob') {
                responseData = await response.blob();
            } else if (responseType === 'text') {
                responseData = await response.text();
            } else {
                // Try to parse as JSON, fallback to text
                const text = await response.text();
                try {
                    responseData = text ? JSON.parse(text) : null;
                } catch {
                    responseData = text;
                }
            }

            // Replicate axios behavior: reject on non-2xx status
            if (!response.ok) {
                const error = createFetchError(`Request failed with status ${response.status}`, response, {
                    url,
                    method,
                    data,
                    headers,
                    responseType,
                    includeToken,
                });
                error.response!.data = responseData;
                throw error;
            }

            // Return axios-like response structure
            return {
                data: responseData,
                status: response.status,
                statusText: response.statusText,
                headers: response.headers,
                ok: response.ok,
            } as FetchResponse<T>;
        })
        .catch((error) => {
            // Don't reject if request was aborted (for Redux Saga compatibility)
            if (error.name === 'AbortError') {
                // Return a promise that never resolves (saga will handle this)
                return new Promise<FetchResponse<T>>(() => {});
            }
            throw error;
        });

    // Create cancelable promise
    const cancelablePromise = fetchPromise as CancelablePromise<FetchResponse<T>>;

    // Add cancel method for both manual cancellation and Redux Saga
    const cancel = () => abortController.abort();
    cancelablePromise.cancel = cancel;
    cancelablePromise[CANCEL] = cancel;

    return cancelablePromise;
}

/**
 * Makes an authenticated HTTP request using native fetch API
 * Automatically injects authentication token from auth service
 *
 * @param config - Request configuration (without includeToken)
 * @returns Cancelable promise with fetch response
 */
export function makeAuthenticatedServiceRequest<T = any>(
    config: Omit<RequestConfig, 'includeToken'>,
): CancelablePromise<FetchResponse<T>> {
    if (!auth.isLoggedIn()) {
        const error = new Error('User is not logged in') as any;
        error[CANCEL] = () => {}; // Add CANCEL property for saga compatibility
        return Promise.reject(error) as CancelablePromise<FetchResponse<T>>;
    }

    return makeFetchRequest({ ...config, includeToken: true });
}

/**
 * Makes a public (non-authenticated) HTTP request using native fetch API
 *
 * @param config - Request configuration
 * @returns Cancelable promise with fetch response
 */
export function makePublicServiceRequest<T = any>(config: RequestConfig): CancelablePromise<FetchResponse<T>> {
    return makeFetchRequest({ ...config, includeToken: false });
}
